import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProfile } from '../contexts/ProfileContext';
import { useStreamAuth } from '../hooks/useStreamAuth';
import LoadingSpinner from '../components/auth/LoadingSpinner';
import DeleteProfileModal from '../components/DeleteProfileModal';
import { getMatrixModeStatus, getMatrixTimeMessage } from '../utils/timeUtils';

const ProfileSelectionPage = () => {
  const { loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  const { isAuthenticated, user } = useStreamAuth();
  const {
    profiles,
    currentProfile,
    remainingProfiles,
    isLoading,
    error,
    hasLoadedProfiles,
    loadProfiles,
    createProfile,
    updateProfile,
    deleteProfile,
    regenerateAvatar,
    setActiveProfile,
    canCreateProfile,
  } = useProfile();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [editingProfile, setEditingProfile] = useState(null);
  const [matrixMode, setMatrixMode] = useState({ isActive: false, reason: 'normal', message: '' });
  const [editProfileName, setEditProfileName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, profile: null });

  // Helper function to get profile avatar with Google profile for main account only
  const getProfileAvatar = profile => {
    // Only use Google profile picture for the main account holder (Owner profile)
    // if (profile?.profile_name === 'Owner' && user?.picture) {
    //   return user.picture;
    // }
    // For all other profiles, use ChillFree profile avatar
    if (profile?.profile_thumb) {
      return profile.profile_thumb;
    }
    return null;
  };

  // Load profiles when component mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated && user && !hasLoadedProfiles) {
      loadProfiles();
    }
  }, [isAuthenticated, user, hasLoadedProfiles, loadProfiles]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect();
    }
  }, [isAuthenticated, navigate]);

  // Check Matrix mode status
  useEffect(() => {
    const status = getMatrixModeStatus();
    setMatrixMode(status);
  }, []);

  // Handle profile selection
  const handleProfileSelect = async profile => {
    const success = await setActiveProfile(profile);
    if (success) {
      // Navigate to home page after successful profile selection
      const params = new URLSearchParams(location.search);
      const redirect = params.get('after_selection');
      if (redirect?.length) {
        return navigate(redirect);
      }
      navigate('/');
    }
  };

  // Handle create profile
  const [createError, setCreateError] = useState(null);

  const handleCreateProfile = async e => {
    e.preventDefault();
    if (!newProfileName.trim()) return;

    setIsCreating(true);
    setCreateError(null);

    const result = await createProfile(newProfileName.trim());

    if (result.success) {
      setNewProfileName('');
      setShowCreateForm(false);
      setCreateError(null);
    } else {
      setCreateError(result.error);
    }
    setIsCreating(false);
  };

  // Handle edit profile
  const handleEditProfile = async e => {
    e.preventDefault();
    if (!editProfileName.trim() || !editingProfile) return;

    const success = await updateProfile(editingProfile._id, editProfileName.trim());

    if (success) {
      setEditingProfile(null);
      setEditProfileName('');
    }
  };

  // Handle delete profile
  const handleDeleteProfile = async profile => {
    setDeleteModal({ isOpen: true, profile });
  };

  // Confirm delete profile
  const confirmDeleteProfile = async () => {
    if (deleteModal.profile) {
      await deleteProfile(deleteModal.profile._id);
      setDeleteModal({ isOpen: false, profile: null });
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, profile: null });
  };

  // Handle regenerate avatar
  const handleRegenerateAvatar = async profile => {
    await regenerateAvatar(profile._id);
  };

  // Start editing a profile
  const startEditing = profile => {
    setEditingProfile(profile);
    setEditProfileName(profile.profile_name);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingProfile(null);
    setEditProfileName('');
  };

  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  if (isLoading && !hasLoadedProfiles) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-primary">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen text-white bg-primary">
        <div className="text-center">
          <div className="mb-4 text-6xl">⚠️</div>
          <h2 className="mb-4 text-2xl font-bold">Error Loading Profiles</h2>
          <p className="mb-6 text-white/70">{error}</p>
          <button
            onClick={() => loadProfiles(true)}
            className="px-6 py-2 text-white transition-colors rounded-lg bg-accent hover:bg-accent/80"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Matrix Mode Version */}
      {matrixMode.isActive ? (
        <div className="relative min-h-screen overflow-hidden text-white bg-black">
          {/* Matrix Rain Background Effect */}
          <div className="absolute inset-0 opacity-10">
            <div className="matrix-rain">
              {Array.from({ length: 15 }).map((_, i) => (
                <div
                  key={i}
                  className="matrix-column"
                  style={{
                    left: `${i * 6.67}%`,
                    animationDelay: `${Math.random() * 3}s`,
                    animationDuration: `${4 + Math.random() * 2}s`,
                  }}
                >
                  {Array.from({ length: 15 }).map((_, j) => (
                    <span key={j} className="font-mono text-xs text-green-400 matrix-char">
                      {String.fromCharCode(0x30a0 + Math.random() * 96)}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="container relative z-10 px-6 py-12 mx-auto">
            {/* Matrix Header */}
            <div className="mb-12 text-center">
              <h1 className="mb-4 font-mono text-4xl font-bold text-green-400">
                Choose Your Reality
              </h1>
              <p className="font-mono text-lg text-green-300/70">{getMatrixTimeMessage()}</p>
              <p className="mt-2 font-mono text-sm text-green-500/60">{matrixMode.message}</p>
            </div>

            {/* Matrix Profiles Grid */}
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 gap-6 mb-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {profiles.map(profile => (
                  <div key={profile._id} className="text-center group">
                    {editingProfile && editingProfile._id === profile._id ? (
                      // Matrix Edit Form
                      <div className="p-4 border rounded-lg bg-black/80 border-green-500/30">
                        <form onSubmit={handleEditProfile}>
                          <input
                            type="text"
                            value={editProfileName}
                            onChange={e => setEditProfileName(e.target.value)}
                            className="w-full px-3 py-2 mb-3 font-mono text-sm text-green-400 bg-gray-900 border rounded border-green-500/50"
                            placeholder="Profile name"
                            maxLength={20}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              type="submit"
                              disabled={!editProfileName.trim() || isLoading}
                              className="flex-1 px-2 py-1 font-mono text-xs text-white transition-colors bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditing}
                              className="flex-1 px-2 py-1 font-mono text-xs text-white transition-colors bg-red-600 rounded hover:bg-red-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    ) : (
                      // Matrix Profile Card with Actions
                      <>
                        <div
                          onClick={() => handleProfileSelect(profile)}
                          className="relative transition-transform transform cursor-pointer hover:scale-105"
                        >
                          <div className="relative w-24 h-24 mx-auto mb-3 overflow-hidden transition-colors bg-black border-2 rounded-lg border-green-500/50 hover:border-green-400">
                            <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-green-600/20"></div>
                            {getProfileAvatar(profile) ? (
                              <img
                                src={getProfileAvatar(profile)}
                                alt={profile.profile_name}
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              <div className="flex items-center justify-center w-full h-full font-mono text-2xl font-bold text-green-400">
                                {profile.profile_name.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <h3 className="font-mono text-sm font-medium text-green-300 transition-colors group-hover:text-green-400">
                            {profile.profile_name}
                          </h3>
                        </div>

                        {/* Matrix Profile Actions */}
                        <div className="mt-2 transition-opacity opacity-0 group-hover:opacity-100">
                          <div className="flex justify-center gap-1">
                            <button
                              onClick={() => startEditing(profile)}
                              className="font-mono text-xs transition-colors text-green-400/70 hover:text-green-400"
                              title="Edit profile"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleRegenerateAvatar(profile)}
                              className="font-mono text-xs transition-colors text-green-400/70 hover:text-green-400"
                              title="Regenerate avatar"
                              disabled={isLoading}
                            >
                              🔄
                            </button>
                            {profiles.length > 1 && (
                              <button
                                onClick={() => handleDeleteProfile(profile)}
                                className="font-mono text-xs text-red-400 transition-colors hover:text-red-300"
                                title="Delete profile"
                                disabled={isLoading}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* Matrix Add Profile Button */}
                {canCreateProfile() && !showCreateForm && (
                  <div className="text-center group">
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="relative flex items-center justify-center w-24 h-24 mx-auto mb-3 overflow-hidden transition-all duration-300 transform border-2 rounded-lg border-green-500/30 hover:border-green-400 bg-black/70 hover:bg-black/90 hover:scale-105"
                    >
                      {/* Matrix-style background effect */}
                      <div className="absolute inset-0 transition-opacity duration-300 opacity-0 bg-gradient-to-br from-green-400/10 to-green-600/10 group-hover:opacity-100"></div>

                      {/* Animated border effect */}
                      <div className="absolute inset-0 border rounded-lg border-green-400/20 animate-pulse"></div>

                      {/* Plus icon with glow effect */}
                      <div className="relative z-10">
                        <svg
                          className="w-8 h-8 text-green-400 group-hover:text-green-300 transition-colors duration-300 drop-shadow-[0_0_8px_rgba(34,197,94,0.5)]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </div>

                      {/* Subtle matrix rain effect on hover */}
                      <div className="absolute inset-0 transition-opacity duration-500 opacity-0 group-hover:opacity-20">
                        <div className="matrix-mini-rain">
                          {Array.from({ length: 3 }).map((_, i) => (
                            <div
                              key={i}
                              className="absolute font-mono text-xs text-green-400 animate-pulse"
                              style={{
                                left: `${20 + i * 30}%`,
                                top: `${10 + i * 20}%`,
                                animationDelay: `${i * 0.5}s`,
                              }}
                            >
                              {String.fromCharCode(0x30a0 + Math.random() * 96)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </button>
                    <h3 className="font-mono text-sm font-medium transition-colors duration-300 text-green-500/70 group-hover:text-green-400">
                      Add Profile
                    </h3>
                    <p className="mt-1 font-mono text-xs text-green-600/50">
                      {profiles.length}/5 profiles
                    </p>
                  </div>
                )}

                {/* Matrix Create Form */}
                {showCreateForm && (
                  <div className="text-center">
                    <div className="relative max-w-xs p-6 mx-auto overflow-hidden border-2 rounded-lg shadow-lg bg-black/95 min-w-60 border-green-500/60 shadow-green-500/20">
                      {/* Matrix-style background glow */}
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-green-400/10 to-green-600/10"></div>

                      {/* Animated border effect */}
                      <div className="absolute inset-0 border rounded-lg border-green-400/30 animate-pulse"></div>

                      {/* Form content */}
                      <div className="relative z-10">
                        <form onSubmit={handleCreateProfile} className="space-y-4">
                          <div>
                            <label className="block mb-2 font-mono text-sm text-left text-green-400">
                              Enter the Matrix...
                            </label>
                            <input
                              type="text"
                              value={newProfileName}
                              onChange={e => setNewProfileName(e.target.value)}
                              className="w-full px-4 py-3 font-mono text-sm text-green-400 transition-all duration-300 bg-gray-900 border-2 rounded-md border-green-500/60 focus:border-green-400 focus:outline-none focus:ring-2 focus:ring-green-400/30 placeholder-green-600/50"
                              placeholder="Profile name..."
                              maxLength={20}
                              autoFocus
                            />
                            <div className="mt-1 font-mono text-xs text-right text-green-500/60">
                              {newProfileName.length}/20
                            </div>

                            {/* Error message */}
                            {createError && (
                              <div className="mt-2 font-mono text-xs text-center text-red-400">
                                {createError}
                              </div>
                            )}
                          </div>

                          <div className="flex gap-3 pt-2">
                            <button
                              type="submit"
                              disabled={!newProfileName.trim() || isCreating}
                              className="flex-1 px-4 py-3 font-mono text-sm text-white transition-all duration-300 bg-green-700 border-2 rounded-md shadow-lg hover:bg-green-600 disabled:bg-green-900/50 border-green-500/60 hover:border-green-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-green-500/20"
                            >
                              {isCreating ? (
                                <span className="flex items-center justify-center gap-2">
                                  <div className="w-4 h-4 border-2 border-green-300 rounded-full border-t-transparent animate-spin"></div>
                                  Creating...
                                </span>
                              ) : (
                                'Create'
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowCreateForm(false);
                                setNewProfileName('');
                              }}
                              className="flex-1 px-4 py-3 font-mono text-sm text-white transition-all duration-300 bg-red-700 border-2 rounded-md shadow-lg hover:bg-red-600 border-red-500/60 hover:border-red-400 shadow-red-500/20"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Matrix Profile Limit Info */}
              {!canCreateProfile() && (
                <div className="font-mono text-sm text-center text-green-500/50">
                  {profiles.length >= 5
                    ? 'Maximum number of profiles reached in the Matrix (5/5)'
                    : `Profile limit reached (${profiles.length}/5)`}
                </div>
              )}
            </div>

            {/* Matrix Footer */}
            <div className="mt-12 text-center">
              <p className="font-mono text-sm text-green-500/50">
                "There is no spoon... but there are profiles"
              </p>
            </div>
          </div>

          <style>{`
            @keyframes matrix-fall {
              0% {
                transform: translateY(-100vh);
                opacity: 1;
              }
              100% {
                transform: translateY(100vh);
                opacity: 0;
              }
            }

            .matrix-rain {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              pointer-events: none;
            }

            .matrix-column {
              position: absolute;
              top: -100vh;
              width: 20px;
              height: 100vh;
              animation: matrix-fall linear infinite;
              display: flex;
              flex-direction: column;
              justify-content: space-around;
            }

            .matrix-char {
              display: block;
              opacity: 0.7;
            }
          `}</style>
        </div>
      ) : (
        /* Normal Mode Version */
        <div className="min-h-screen text-white bg-primary">
          <div className="container px-6 py-12 mx-auto">
            {/* Header */}
            <div className="mb-12 text-center">
              <h1 className="mb-4 text-4xl font-bold">Who's watching?</h1>
              <p className="text-lg text-white/70">
                Select a profile to continue with your personalized experience
              </p>
            </div>

            {/* Profiles Grid */}
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 gap-6 mb-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {profiles.map(profile => (
                  <div key={profile._id} className="text-center group">
                    {editingProfile && editingProfile._id === profile._id ? (
                      // Edit Form
                      <div className="p-4 rounded-lg bg-secondary">
                        <form onSubmit={handleEditProfile}>
                          <input
                            type="text"
                            value={editProfileName}
                            onChange={e => setEditProfileName(e.target.value)}
                            className="w-full px-3 py-2 mb-3 text-sm text-white bg-gray-700 rounded"
                            placeholder="Profile name"
                            maxLength={20}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              type="submit"
                              disabled={!editProfileName.trim() || isLoading}
                              className="flex-1 px-2 py-1 text-xs text-white transition-colors rounded bg-accent hover:bg-accent/80 disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditing}
                              className="flex-1 px-2 py-1 text-xs text-white transition-colors bg-gray-600 rounded hover:bg-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    ) : (
                      // Profile Card
                      <>
                        <div
                          onClick={() => handleProfileSelect(profile)}
                          className="relative transition-transform transform cursor-pointer hover:scale-105"
                        >
                          <div className="w-24 h-24 mx-auto mb-3 overflow-hidden transition-colors bg-gray-700 border-2 border-transparent rounded-lg hover:border-accent">
                            {getProfileAvatar(profile) ? (
                              <img
                                src={getProfileAvatar(profile)}
                                alt={profile.profile_name}
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              <div className="flex items-center justify-center w-full h-full text-2xl font-bold text-white/70">
                                {profile.profile_name.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <h3 className="text-sm font-medium text-white transition-colors group-hover:text-accent">
                            {profile.profile_name}
                          </h3>
                        </div>

                        {/* Profile Actions */}
                        <div className="mt-2 transition-opacity opacity-0 group-hover:opacity-100">
                          <div className="flex justify-center gap-1">
                            <button
                              onClick={() => startEditing(profile)}
                              className="text-xs transition-colors text-white/70 hover:text-white"
                              title="Edit profile"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleRegenerateAvatar(profile)}
                              className="text-xs transition-colors text-white/70 hover:text-white"
                              title="Regenerate avatar"
                              disabled={isLoading}
                            >
                              🔄
                            </button>
                            {profiles.length > 1 && (
                              <button
                                onClick={() => handleDeleteProfile(profile)}
                                className="text-xs text-red-400 transition-colors hover:text-red-300"
                                title="Delete profile"
                                disabled={isLoading}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* Add Profile Button */}
                {canCreateProfile() && (
                  <div className="text-center">
                    {showCreateForm ? (
                      // Create Form
                      <div className="p-4 rounded-lg bg-secondary">
                        <form onSubmit={handleCreateProfile}>
                          <input
                            type="text"
                            value={newProfileName}
                            onChange={e => setNewProfileName(e.target.value)}
                            className="w-full px-3 py-2 mb-3 text-sm text-white bg-gray-700 rounded"
                            placeholder="Profile name"
                            maxLength={20}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              type="submit"
                              disabled={!newProfileName.trim() || isCreating}
                              className="flex-1 px-2 py-1 text-xs text-white transition-colors rounded bg-accent hover:bg-accent/80 disabled:opacity-50"
                            >
                              {isCreating ? 'Creating...' : 'Create'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowCreateForm(false);
                                setNewProfileName('');
                              }}
                              className="flex-1 px-2 py-1 text-xs text-white transition-colors bg-gray-600 rounded hover:bg-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    ) : (
                      // Add Button
                      <div
                        onClick={() => setShowCreateForm(true)}
                        className="transition-transform transform cursor-pointer hover:scale-105"
                      >
                        <div className="flex items-center justify-center w-24 h-24 mx-auto mb-3 transition-colors bg-gray-700 border-2 border-gray-500 border-dashed rounded-lg hover:border-accent">
                          <span className="text-3xl text-gray-400 transition-colors hover:text-accent">
                            +
                          </span>
                        </div>
                        <h3 className="text-sm font-medium text-gray-400 transition-colors hover:text-accent">
                          Add Profile
                        </h3>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Profile Limit Info */}
              {remainingProfiles === 0 && (
                <div className="text-sm text-center text-white/50">
                  Maximum number of profiles reached
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-12 text-center">
              <p className="text-sm text-white/50">
                Each profile has its own watchlist and viewing history
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Profile Modal */}
      <DeleteProfileModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteProfile}
        profileName={deleteModal.profile?.profile_name || ''}
      />
    </>
  );
};

export default ProfileSelectionPage;
